import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';
import useJsonLd, { articleJsonLd } from '@/lib/jsonLd';
import { mainSiteUrl } from '@/mocks/categories';

const faqItems = [
  { q: 'article.faq.q1', a: 'article.faq.a1' },
  { q: 'article.faq.q2', a: 'article.faq.a2' },
  { q: 'article.faq.q3', a: 'article.faq.a3' },
  { q: 'article.faq.q4', a: 'article.faq.a4' },
];

export default function Article() {
  const { t } = useTranslation();
  const articlePath = '/allchinabuy-spreadsheet-guide';
  const articleTitle = t('article.title');
  const articleDescription = t('article.quickAnswer.text');
  usePageMeta({
    title: articleTitle,
    description: articleDescription,
    keywords:
      'AllChinaBuy Spreadsheet guide, AllChinaBuy product finds, AllChinaBuy categories, AllChinaBuy buying guide',
    path: articlePath,
    type: 'article',
  });
  useJsonLd(
    articleJsonLd({
      path: articlePath,
      title: articleTitle,
      description: articleDescription,
    }),
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <article className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-16">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700 cursor-pointer">
              {t('nav.brand')}
            </Link>
            <span>/</span>
            <span className="text-foreground-400">{t('article.title')}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
              <i className="ri-book-read-line"></i>
              {t('nav.guides')}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {t('article.title')}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {t('article.readTime')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line"></i>
                {t('article.updated')}
              </span>
            </div>
          </header>

          <div className="mt-8 rounded-xl border border-primary-200 bg-primary-50 p-5">
            <p className="flex items-center gap-2 text-sm font-bold text-primary-800">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-flashlight-line"></i>
              </span>
              {t('article.quickAnswer.label')}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              {t('article.quickAnswer.text')}
            </p>
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t('article.s1.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s1.p1')}
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s1.p2')}
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t('article.s2.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s2.p1')}
            </p>
            <ul className="mt-4 space-y-3">
              {(['li1', 'li2', 'li3', 'li4'] as const).map((li, i) => (
                <li key={li} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <span className="text-xs font-bold">{i + 1}</span>
                  </span>
                  {t(`article.s2.${li}`)}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t('article.s3.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s3.p1')}
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s3.p2')}
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t('article.s4.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s4.p1')}
            </p>
            <ul className="mt-4 space-y-3">
              {(['li1', 'li2', 'li3', 'li4'] as const).map((li) => (
                <li key={li} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-700">
                    <i className="ri-check-line"></i>
                  </span>
                  {t(`article.s4.${li}`)}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t('article.s5.title')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s5.p1')}
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t('article.s5.p2')}
            </p>
          </section>

          <div className="mt-12 rounded-xl border border-background-200/70 bg-background-100 p-6">
            <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <span className="flex h-5 w-5 items-center justify-center text-primary-700">
                <i className="ri-links-line"></i>
              </span>
              {t('article.related.title')}
            </p>
            <Link
              to="/"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.related.home')}
            </Link>
          </div>
        </article>

        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-3xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('article.faq.title')}
            </h2>
            <FaqList />
          </div>
        </section>

        <section className="bg-primary-500">
          <div className="mx-auto w-full max-w-4xl px-4 py-14 text-center md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">
              {t('article.cta.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
              {t('article.cta.subtitle')}
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 sm:w-auto cursor-pointer"
              >
                {t('article.cta.button1')}
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto cursor-pointer"
              >
                {t('article.cta.button2')}
              </a>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/70 underline-offset-4 hover:underline cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function FaqList() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="mt-8 space-y-3">
      {faqItems.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <div
            key={item.q}
            className="overflow-hidden rounded-xl border border-background-200/70 bg-white"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
            >
              <span className="font-heading text-sm font-semibold text-foreground-900 md:text-base">
                {t(item.q)}
              </span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-500">
                <i className={`ri-add-line text-sm transition-transform ${isOpen ? 'rotate-45' : ''}`}></i>
              </span>
            </button>
            {isOpen && (
              <p className="px-6 pb-5 text-sm leading-relaxed text-foreground-600">{t(item.a)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}